import React, { useState, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import DiagramModal from './DiagramModal';
import ShareModal from './ShareModal';
// Импорт иконок Material UI
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import MouseIcon from '@mui/icons-material/Mouse';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TitleIcon from '@mui/icons-material/Title';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CodeIcon from '@mui/icons-material/Code';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NoteIcon from '@mui/icons-material/Note';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IosShareIcon from '@mui/icons-material/IosShare';
import HistoryIcon from '@mui/icons-material/History';
import { SiGit } from 'react-icons/si';
import { useNavigate } from 'react-router-dom';

const ToolbarButton = ({ title, onClick, active, children, style = {} }) => {
    const { theme } = useTheme();

    return (
        <button
            title={title}
            onClick={onClick}
            style={{
                background: active ? theme.toolbar.buttonBackgroundActive : theme.toolbar.buttonBackground,
                color: theme.toolbar.buttonText,
                border: `1px solid ${theme.toolbar.buttonBorder}`,
                borderRadius: 6,
                padding: '6px 8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                minWidth: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...style
            }}
            onMouseEnter={(e) => {
                if (!active) {
                    e.target.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151';
                }
            }}
            onMouseLeave={(e) => {
                if (!active) {
                    e.target.style.backgroundColor = theme.toolbar.buttonBackground;
                }
            }}
        >
            {children}
        </button>
    );
};

const Toolbar = ({
    tool,
    setTool,
    zoomIn,
    zoomOut,
    fitToBoard,
    centerToSelection,
    showCodePanel,
    setShowCodePanel,
    elements,
    onLoadDiagram,
    currentDiagramId,
    zoom,
    scrollPosition,
    topOffset = 0,
    currentDiagramMeta,
    enableCode = true,
    onExportImage, // новый проп
    code,
    setCode,
    onSyncToCode,
    onSaveDiagram,
    onOpenDiagramManager,
    onPublishDiagram,
    isPublishing = false,
    onShowHistory,
    diagramGitlabPath,
    onBeforeNavigate,
}) => {
    const { theme, toggleTheme, currentTheme } = useTheme();
    const [showDiagramModal, setShowDiagramModal] = useState(false);
    const [initialTab, setInitialTab] = useState('list');
    const [showShareModal, setShowShareModal] = useState(false);
    const navigate = useNavigate();
    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportButtonRef = React.useRef(null);
    const logoSrc = `${process.env.PUBLIC_URL || ''}/logo_base.jpg`;

    const guardedNavigate = (target) => {
        if (typeof onBeforeNavigate === 'function' && !onBeforeNavigate()) return;
        navigate(target);
    };

    const handleNewDiagram = () => {
        if (typeof onSaveDiagram === 'function') {
            onSaveDiagram();
            return;
        }
        // Открываем модальное окно сразу на вкладке "Сохранить", чтобы выбрать тип и имя
        setInitialTab('save');
        setShowDiagramModal(true);
    };

    const iconStyle = { width: 18, height: 18 };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportButtonRef.current && !exportButtonRef.current.contains(event.target)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <>
            <div
                style={{
                    position: 'absolute',
                    top: topOffset,
                    left: 0,
                    right: 0,
                    height: 56,
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.toolbar.background,
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                    borderBottomColor: theme.toolbar.border,
                    padding: '0 16px',
                    boxShadow: theme.toolbar.shadow
                }}
            >
                {/* Левая секция - Управление диаграммами */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ToolbarButton title="Назад" onClick={() => guardedNavigate(-1)}>
                        <ArrowBackIcon style={iconStyle} />
                    </ToolbarButton>

                    <div
                        onClick={() => guardedNavigate('/projects')}
                        title="На главную"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 10,
                            fontSize: 18,
                            fontWeight: 'bold',
                            color: theme.toolbar.text,
                            marginRight: 16,
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}
                    >
                        <img
                            src={logoSrc}
                            alt=""
                            aria-hidden="true"
                            style={{ width: 30, height: 30, objectFit: 'contain', borderRadius: 6, background: '#fff' }}
                        />
                        Aspect
                    </div>

                    <ToolbarButton title="Новая диаграмма" onClick={handleNewDiagram}>
                        <DescriptionIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton title="Управление диаграммами" onClick={() => {
                        if (typeof onOpenDiagramManager === 'function') onOpenDiagramManager();
                        else setShowDiagramModal(true);
                    }}>
                        <SaveIcon style={iconStyle} />
                    </ToolbarButton>

                    {onPublishDiagram && (
                        <ToolbarButton
                            title={isPublishing ? 'Публикуется...' : 'Опубликовать в GitLab'}
                            onClick={onPublishDiagram}
                            style={{ color: isPublishing ? undefined : '#f05032', borderColor: isPublishing ? undefined : '#f05032', opacity: isPublishing ? 0.6 : 1 }}
                        >
                            <SiGit style={iconStyle} />
                        </ToolbarButton>
                    )}

                    {onShowHistory && diagramGitlabPath && (
                        <ToolbarButton
                            title="История версий"
                            onClick={onShowHistory}
                            style={{ color: '#a6e3a1', borderColor: '#40a02b' }}
                        >
                            <HistoryIcon style={iconStyle} />
                        </ToolbarButton>
                    )}

                    {onExportImage && (
                        <div style={{ position: 'relative' }} ref={exportButtonRef}>
                            <ToolbarButton
                                title="Сохранить как изображение"
                                onClick={() => setShowExportMenu(!showExportMenu)}
                                active={showExportMenu}
                            >
                                <ImageIcon style={iconStyle} />
                            </ToolbarButton>
                            {showExportMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: 4,
                                    background: theme.toolbar.background,
                                    border: `1px solid ${theme.toolbar.border}`,
                                    borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 3000,
                                    minWidth: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: '4px 0'
                                }}>
                                    {['png', 'jpg', 'svg'].map(format => (
                                        <button
                                            key={format}
                                            onClick={() => {
                                                onExportImage(format);
                                                setShowExportMenu(false);
                                            }}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                color: theme.toolbar.buttonText,
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                textTransform: 'uppercase',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = theme.name === 'light' ? '#f1f5f9' : '#374151'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        >
                                            {format}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <ToolbarButton
                        title={currentDiagramId ? 'Share diagram' : 'Save diagram first to share'}
                        onClick={() => setShowShareModal(true)}
                        style={{ opacity: currentDiagramId ? 1 : 0.4 }}
                    >
                        <IosShareIcon style={iconStyle} />
                    </ToolbarButton>

                    <div style={{ width: 1, height: 24, backgroundColor: theme.toolbar.border, margin: '0 8px' }} />

                    {/* Инструменты рисования */}
                    <ToolbarButton
                        title="Выбор (S)"
                        onClick={() => setTool('select')}
                        active={tool === 'select'}
                    >
                        <MouseIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton
                        title="Прямоугольник (R)"
                        onClick={() => setTool('rectangle')}
                        active={tool === 'rectangle'}
                    >
                        <CropSquareIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton
                        title="Круг (C)"
                        onClick={() => setTool('circle')}
                        active={tool === 'circle'}
                    >
                        <RadioButtonUncheckedIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton
                        title="Стрелка (A)"
                        onClick={() => setTool('arrow')}
                        active={tool === 'arrow'}
                    >
                        <ArrowForwardIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton
                        title="Текст (T)"
                        onClick={() => setTool('text')}
                        active={tool === 'text'}
                    >
                        <TitleIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton
                        title="Заметка (N)"
                        onClick={() => setTool('note')}
                        active={tool === 'note'}
                    >
                        <NoteIcon style={iconStyle} />
                    </ToolbarButton>
                </div>

                {/* Центральная секция - Навигация */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ToolbarButton title="Увеличить масштаб (+)" onClick={zoomIn}>
                        <ZoomInIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton title="Уменьшить масштаб (-)" onClick={zoomOut}>
                        <ZoomOutIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton title="Вместить все (0)" onClick={fitToBoard}>
                        <CenterFocusStrongIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton title="К выделенному" onClick={centerToSelection}>
                        <GpsFixedIcon style={iconStyle} />
                    </ToolbarButton>
                </div>

                {/* Правая секция - Настройки */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {onSyncToCode && (
                        <ToolbarButton
                            title="Синхронизировать холст → DSL код"
                            onClick={onSyncToCode}
                            style={{ fontSize: 11, padding: '6px 10px', gap: 4 }}
                        >
                            ← Sync to Code
                        </ToolbarButton>
                    )}
                    <ToolbarButton
                        title={showCodePanel ? 'Скрыть код' : 'Показать код'}
                        onClick={() => enableCode && setShowCodePanel(!showCodePanel)}
                        active={showCodePanel}
                        style={{ opacity: enableCode ? 1 : 0.3 }}
                    >
                        <CodeIcon style={iconStyle} />
                    </ToolbarButton>

                    <ToolbarButton
                        title={`Переключить тему (${currentTheme})`}
                        onClick={toggleTheme}
                    >
                        {currentTheme === 'dark' ? <LightModeIcon style={iconStyle} /> : <DarkModeIcon style={iconStyle} />}
                    </ToolbarButton>

                    {elements.length > 0 && (
                        <div style={{
                            color: theme.toolbar.text,
                            fontSize: 12,
                            opacity: 0.7,
                            marginLeft: 8
                        }}>
                            {elements.length} элементов • тип: {currentDiagramMeta?.type || 'uml'}
                        </div>
                    )}
                </div>
            </div>

            {/* Модальное окно управления диаграммами */}
            {showDiagramModal && (
                <DiagramModal
                    visible={showDiagramModal}
                    onClose={() => setShowDiagramModal(false)}
                    elements={elements}
                    onLoadDiagram={onLoadDiagram}
                    currentDiagramId={currentDiagramId}
                    zoom={zoom}
                    scrollPosition={scrollPosition}
                    currentDiagramMeta={currentDiagramMeta}
                    initialTab={initialTab}
                    code={code}
                    setCode={setCode}
                />
            )}

            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                diagramId={currentDiagramId}
                diagramName={currentDiagramMeta?.name}
                diagramType={currentDiagramMeta?.type}
            />
        </>
    );
};

export default Toolbar;
